import React, { useState, useEffect } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enGB } from "date-fns/locale";
import { Box, TextField, Popover, Button, Typography, InputAdornment } from "@mui/material";
import { format, isValid } from "date-fns";
import dayjs from "dayjs";
import { IconCalendarWeek, IconX } from "@tabler/icons-react";
import { IconButton } from "@mui/material";

export default function CustomRangePicker({ onChange, defaultRange, hideLabel, isClearable = true }) {
  // Date validation function
  const getValidDate = (date) => {
    if (!date) return null;

    // Handle Day.js object
    if (date && typeof date === 'object' && dayjs.isDayjs(date)) {
      const jsDate = date.toDate();
      return isValid(jsDate) ? jsDate : null;
    }

    // Handle other date types
    try {
      const jsDate = new Date(date);
      return isValid(jsDate) ? jsDate : null;
    } catch (error) {
      return null;
    }
  };

  const initialRange = [
    {
      startDate: getValidDate(defaultRange?.startDate) || null,
      endDate: getValidDate(defaultRange?.endDate) || null,
      key: "selection",
    },
  ];

  const [range, setRange] = useState(initialRange);
  const [tempRange, setTempRange] = useState(initialRange);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (defaultRange) {
      const updated = [
        {
          startDate: getValidDate(defaultRange.startDate) || null,
          endDate: getValidDate(defaultRange.endDate) || null,
          key: "selection",
        },
      ];
      setRange(updated);
      setTempRange(updated);
    } else {
      // Clear if no default range
      const cleared = [{ startDate: null, endDate: null, key: "selection" }];
      setRange(cleared);
      setTempRange(cleared);
    }
  }, [defaultRange]);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setTempRange(range);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOk = () => {
    setRange(tempRange);
    handleClose();
    if (onChange) {
      onChange({
        startDate: tempRange[0].startDate,
        endDate: tempRange[0].endDate,
      });
    }
  };

  const handleClear = () => {
    const cleared = [{ startDate: null, endDate: null, key: "selection" }];
    setRange(cleared);
    setTempRange(cleared);
    handleClose();
    if (onChange) {
      onChange({ startDate: null, endDate: null });
    }
  };

  const start = range[0].startDate ? format(range[0].startDate, "dd/MM/yyyy") : "";
  const end = range[0].endDate ? format(range[0].endDate, "dd/MM/yyyy") : "";
  const displayValue = start && end ? `${start} - ${end}` : "";

  return (
    <>

      {!hideLabel && <Typography fontWeight="bold" mb={1}>Date Range</Typography>}
      <TextField
        value={displayValue}
        onClick={handleOpen}
        size="medium"
        fullWidth
        placeholder="Select date range"
        readOnly
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconCalendarWeek sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: (displayValue && isClearable) ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <IconX size="1rem" />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ '& .MuiPopover-paper': { borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>Select Date Range</Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <DateRange
              locale={enGB}
              editableDateInputs={false}
              onChange={(item) => setTempRange([item.selection])}
              moveRangeOnFirstSelection={false}
              ranges={tempRange}
              months={1}
              direction="horizontal"
              rangeColors={['#3B82F6']}
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              monthDisplayFormat="MMM yyyy"
              weekdayDisplayFormat="EEEEEE"
              className={
                !tempRange[0].startDate && !tempRange[0].endDate ? 'hide-selection' : ''
              }
            />



          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
            <Typography variant="body2" color="textSecondary">
              {tempRange[0].startDate && tempRange[0].endDate
                ? `${format(tempRange[0].startDate, "MMM dd, yyyy")} - ${format(tempRange[0].endDate, "MMM dd, yyyy")}`
                : "Select a date range"
              }
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {isClearable && (
                <Button size="small" onClick={handleClear} variant="outlined" sx={{ borderColor: '#D1D5DB', color: '#374151', '&:hover': { borderColor: '#9CA3AF', backgroundColor: 'rgba(0,0,0,0.04)' } }}>Clear</Button>
              )}
              <Button size="small" onClick={handleOk} variant="contained" sx={{ backgroundColor: '#3B82F6', '&:hover': { backgroundColor: '#2563EB' } }}>Apply</Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
}